import { asyncHandler } from '../async-handler';
import store from '../../../store/store';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import { UPDATE_SHOP_API } from '../../constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../utils/body-builder';

export interface UpdateShopNameResponse {
  success: boolean;
  data?: {
    slug: string;
    id: number;
    name: string;
  };
  message?: string;
}

/**
 * Rename a brand (shop). Hits `PUT /api/v1/shops/{slug}` with a single `name`
 * field. The slug is intentionally NOT changed — it is the stable scoping key
 * used across the app (active_shop_slug / accessible_shops). Only the display
 * name changes.
 */
export async function updateShopName(
  shopSlug: string,
  name: string
): Promise<UpdateShopNameResponse> {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const isWhiteLabel = state.UI.isWhiteLabel;

  const requestInfo: RequestInfo = {
    url: UPDATE_SHOP_API(shopSlug),
    method: 'PUT',
    body: applicationWWWXRLEncodedBodyBuilder({ name }),
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/x-www-form-urlencoded',
      Authorization: `${accessToken.type} ${
        isWhiteLabel
          ? accessToken.currentWhiteLabelSellerToken
          : accessToken.sellerToken
      }`,
    },
  };

  const actionType = 'UPDATE_SHOP';
  const customMsg = 'Brand name updated.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false,
    false,
    true
  );

  const response = res?.data;

  if (response?.success === true) {
    return {
      success: true,
      data: response.data,
    };
  }

  const errorMessage =
    response?.message ||
    (Array.isArray(response?.errors)
      ? response.errors.join(' ')
      : typeof response?.errors === 'object'
      ? Object.values(response.errors).join(' ')
      : '') ||
    'Failed to update brand name.';

  return {
    success: false,
    message: errorMessage,
  };
}
